import consts from "shared/consts";
import Konva from "konva";
import { isElementChild } from "frontend/utils/canvas-utils";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import { Predicate, countByPredicates } from "shared/util/fn-utils";
import { getDerivedGroupId } from "utils/node-utils";
import { useShowMondayFeatures } from "frontend/canvas-page/hooks/use-show-monday-features";
import { isElementInGantt } from "frontend/canvas-designer-new/elements/canvas-elements-utils";

// eslint-disable-next-line security/detect-non-literal-regexp
const mondayConvertibleElements = new RegExp(
  [
    ...Object.values(consts.SHAPES),
    consts.CANVAS_ELEMENTS.STICKY_NOTE,
    consts.CANVAS_ELEMENTS.TEXT_BLOCK,
    consts.CANVAS_ELEMENTS.SHAPE,
  ].join("|")
);

const elementPredicates: Record<string, Predicate<Konva.Node>> = {
  isTaskCard: (element) =>
    (element.attrs.type === consts.CANVAS_ELEMENTS.TASK_CARD || element.attrs.type === consts.CANVAS_ELEMENTS.TASK) &&
    !element.attrs.element?.containerId,
  isGanttCard: (node) => isElementInGantt({ ...node.attrs.element, type: node.attrs.type }, true),
  isConvertibleToMondayItem: (element) => mondayConvertibleElements.test(element.attrs.type),
  hasText: (element) => !!element.attrs.element.text || element.attrs.element.title, // test the text field of shapes and task-cards
} as const;

export function useConvertToMonday(nodes: Konva.Node[]) {
  const mondayItemFromGanttCards = useFeatureFlag("monday-item-from-gantt-card");
  const mondayItemFromTaskCards = useFeatureFlag("monday-item-from-task-card");
  const showMondayFeatures = useShowMondayFeatures();

  const count = countByPredicates(elementPredicates, nodes);
  const totalElements = nodes.length;

  const hasMondayConvertibleElement =
    (count.isTaskCard > 0 && mondayItemFromTaskCards) ||
    (count.isGanttCard > 0 && mondayItemFromGanttCards) ||
    count.isConvertibleToMondayItem > 0;

  const elementsAreOfSameType =
    count.isTaskCard === totalElements ||
    count.isGanttCard === totalElements ||
    count.isConvertibleToMondayItem === totalElements;
  const containsTimelineChild = nodes.some((node) => isElementChild(node, "timeline"));
  const allElementsHaveText = count.hasText === totalElements;
  // Enable when we have a limit
  const isBelowLimit = true; // totalElements <= consts.ACCOUNT_LIMITS.CONVERT_TO_MONDAY_ITEMS;

  const hasDataLayer = nodes.some((node) => getDerivedGroupId(node));

  const enabled = elementsAreOfSameType && allElementsHaveText && !containsTimelineChild;
  const visible = showMondayFeatures && hasMondayConvertibleElement && !hasDataLayer && isBelowLimit;

  let message: string | null = null;
  if (!elementsAreOfSameType || containsTimelineChild) {
    message = "Cannot convert current selection";
  } else if (!allElementsHaveText) {
    if (totalElements === 1) {
      message = "Add text to convert this element";
    } else {
      message = "Add text to all elements to convert them";
    }
  }

  return { visible, enabled, message };
}
